/**
 * @fileoverview Secure admin route using environment-based path segment
 * @module routes/sys/mgmt/$segment
 */

import type { ReactElement } from 'react';
import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router';
import { AdminGuard } from '@/features/admin';

/**
 * Admin route layout component with secure path segment validation.
 *
 * This route uses an environment variable to determine the valid path segment,
 * preventing access via simple URLs like /admin. The actual path is:
 * `/sys/mgmt/${VITE_ADMIN_PATH_SEGMENT}`
 *
 * **Security Features:**
 * - Obscured URL path using environment variable
 * - Client-side route guard (AdminGuard)
 * - Server-side protection via database RLS policies
 * - Path segment validation before rendering admin content
 *
 * **Usage:**
 * 1. Set VITE_ADMIN_PATH_SEGMENT in .env file
 * 2. Access admin panel via /sys/mgmt/{your_segment}
 * 3. Invalid segments redirect to homepage
 *
 * @component
 */
function AdminRoute(): ReactElement {
  // Get the route parameters
  const { segment } = Route.useParams();

  // Get the expected segment from environment variable
  const expectedSegment = import.meta.env.VITE_ADMIN_PATH_SEGMENT;

  // Validate segment matches expected value
  if (!expectedSegment || segment !== expectedSegment) {
    // Redirect to homepage if segment is invalid
    return <Navigate to="/" />;
  }

  // Render outlet for child routes with guard
  return (
    <AdminGuard>
      <Outlet />
    </AdminGuard>
  );
}

/**
 * File-based route configuration for TanStack Router.
 *
 * Creates route: /sys/mgmt/$segment
 * - $segment is a dynamic parameter validated against environment variable
 * - Only renders admin content when segment matches VITE_ADMIN_PATH_SEGMENT
 */
export const Route = createFileRoute('/sys/mgmt/$segment')({
  component: AdminRoute,
});
